import { ProductImageComponent } from '../shared/product-image/product-image.component'
import {
  CustomFieldFacet,
  CustomFieldFilter,
  CustomFieldSummary,
} from '../../types/product-custom-fields'
import {
  normalizeCustomFieldIdentity,
  serializeCustomFieldFilters,
} from '../../helpers/custom-fields'
import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core'
import { CdkTrapFocus } from '@angular/cdk/a11y'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { PaginatorIntl } from '../intl/paginator-intl'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { Subscription } from 'rxjs'
import {
  MarketplaceFilters,
  MarketplaceFacets,
  MarketplaceProduct,
} from '../../types/marketplace'
import { MarketplaceService } from '../../services/marketplace.service'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { TranslationService } from '../../services/translation.service'
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies'
import { PRODUCT_CONDITIONS } from '../../constants/product-conditions'

type CustomFieldControl = CustomFieldFacet & {
  search: string
  loading: boolean
  failed: boolean
  hasMore: boolean
}

const emptyFacets: MarketplaceFacets = {
  customFields: [],
  categories: [],
  subcategories: [],
  brands: [],
  suppliers: [],
  conditions: [],
  weightUnits: [],
  droneTypes: [],
  videoResolutions: [],
  sensorSizes: [],
  currencies: [],
}

function defaultFilters(): MarketplaceFilters {
  return {
    customFields: [],
    search: '',
    category: '',
    subcategory: '',
    brand: [],
    supplier: [],
    condition: [],
    droneType: [],
    videoResolution: [],
    sensorSize: [],
    flightRange: [],
    fpv: [],
    currency: '',
    minPrice: null,
    maxPrice: null,
    includeUnpriced: true,
    weightUnit: '',
    minWeight: null,
    maxWeight: null,
    sort: 'newest',
    page: 1,
    pageSize: 6,
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-marketplace',
  imports: [
    ProductImageComponent,
    FormsModule,
    ReactiveFormsModule,
    CdkTrapFocus,
    MatAutocompleteModule,
    RouterLink,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
  templateUrl: './marketplace.component.html',
  styleUrl: './marketplace.component.scss',
})
export class MarketplaceComponent implements OnInit, OnDestroy {
  @ViewChild('filterButton', { read: ElementRef })
  filterButton?: ElementRef<HTMLButtonElement>
  marketplace = inject(MarketplaceService)
  private cdr = inject(ChangeDetectorRef)
  private routeService = inject(RouteService)
  t = inject(TranslationService)
  currencies = CURRENCIES
  productConditions = PRODUCT_CONDITIONS
  filters = defaultFilters()
  draft = defaultFilters()
  facets = emptyFacets
  products: MarketplaceProduct[] = []
  totalRecords = 0
  loading = false
  failed = false
  filterPanelOpen = false
  brandSearch = new FormControl('', { nonNullable: true })
  sellerSearch = new FormControl('', { nonNullable: true })
  customFieldSearch = new FormControl<string | CustomFieldSummary>('', {
    nonNullable: true,
  })
  customFieldSuggestions: CustomFieldSummary[] = []
  customFieldSearchLoading = false
  customFieldSearchFailed = false
  customFieldSearchHasMore = false
  customFieldSearchHasResults = false
  selectedCustomFields: CustomFieldControl[] = []
  private appliedCustomFields: CustomFieldControl[] = []
  view: 'grid' | 'list' = 'grid'
  quantityDrafts: Record<string, string | undefined> = {}
  invalidQuantityId: string | null = null
  invalidCustomRangeInputs = new Map<
    HTMLInputElement,
    { facet: CustomFieldFacet; bound: 'min' | 'max' }
  >()
  private sellerNames = new Map<string, string>()
  private request?: Subscription
  private timer?: ReturnType<typeof setTimeout>
  private customRangeTimer?: ReturnType<typeof setTimeout>
  private customFieldSearchTimer?: ReturnType<typeof setTimeout>
  private customFieldSearchRequest?: Subscription
  private customFieldSearchChanges?: Subscription
  private customOptionRequests = new Map<string, Subscription>()
  private customOptionTimers = new Map<string, ReturnType<typeof setTimeout>>()

  ngOnInit(): void {
    this.customFieldSearchChanges =
      this.customFieldSearch.valueChanges.subscribe((query) =>
        this.searchCustomFields(query),
      )
    this.marketplace.loadCart()
    this.routeService.setRoute(
      this.routeService.getUserRoutes(
        ROUTES.MARKETPLACE.title,
        ROUTES.MARKETPLACE.path,
      ),
    )
    this.load()
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe()
    if (this.timer) clearTimeout(this.timer)
    this.clearCustomRangeTimer()
    this.clearCustomFieldSearch()
    this.customFieldSearchChanges?.unsubscribe()
    this.cancelCustomOptionRequests()
  }

  load(): void {
    this.request?.unsubscribe()
    this.loading = true
    this.failed = false
    this.cdr.markForCheck()
    this.request = this.marketplace.browse(this.filters).subscribe({
      next: (response) => {
        try {
          const facets = response.facets
          this.products = response.results ?? []
          this.totalRecords = response.totalRecords ?? 0
          this.facets = {
            customFields: facets?.customFields ?? [],
            categories: facets?.categories ?? [],
            subcategories: facets?.subcategories ?? [],
            brands: facets?.brands ?? [],
            suppliers: facets?.suppliers ?? [],
            conditions: facets?.conditions ?? [],
            weightUnits: facets?.weightUnits ?? [],
            droneTypes: facets?.droneTypes ?? [],
            videoResolutions: facets?.videoResolutions ?? [],
            sensorSizes: facets?.sensorSizes ?? [],
            currencies: facets?.currencies ?? [],
          }
          for (const seller of this.facets.suppliers) {
            if (seller?.id) this.sellerNames.set(seller.id, seller.name)
          }
        } catch (error) {
          console.error('Unable to display marketplace results', error)
          this.failed = true
          this.products = []
          this.totalRecords = 0
          this.facets = emptyFacets
        } finally {
          this.loading = false
          this.cdr.markForCheck()
        }
      },
      error: () => {
        this.loading = false
        this.failed = true
        this.products = []
        this.totalRecords = 0
        this.facets = emptyFacets
        this.cdr.markForCheck()
      },
    })
  }

  private scheduleLoad(): void {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), 250)
  }

  searchChanged(): void {
    this.draft.search = this.filters.search
    this.filters.page = 1
    this.scheduleLoad()
  }

  categoryChanged(category: string): void {
    this.clearCustomRangeTimer()
    this.filters.category = category
    this.filters.subcategory = ''
    this.filters.page = 1
    if (category !== 'Drones') {
      this.filters.droneType = []
      this.filters.videoResolution = []
      this.filters.sensorSize = []
      this.filters.flightRange = []
      this.filters.fpv = []
    }
    this.draft = structuredClone(this.filters)
    this.resetCustomRangeInputs()
    this.brandSearch.setValue('')
    this.sellerSearch.setValue('')
    this.load()
  }

  subcategoryChanged(subcategory: string): void {
    this.clearCustomRangeTimer()
    this.filters.subcategory = subcategory
    this.filters.page = 1
    this.draft = structuredClone(this.filters)
    this.resetCustomRangeInputs()
    this.load()
  }

  sortChanged(): void {
    this.clearCustomRangeTimer()
    this.filters.page = 1
    this.draft = structuredClone(this.filters)
    this.resetCustomRangeInputs()
    this.load()
  }

  openFilters(): void {
    this.clearCustomRangeTimer()
    this.draft = structuredClone(this.filters)
    this.restoreCustomFieldControls()
    this.resetCustomRangeInputs()
    this.filterPanelOpen = true
  }

  closeFilters(): void {
    this.filterPanelOpen = false
    this.draft = structuredClone(this.filters)
    this.restoreCustomFieldControls()
    this.resetCustomRangeInputs()
    this.clearCustomFieldSearch()
    this.filterButton?.nativeElement.focus()
  }

  applyFilters(): void {
    if (
      this.invalidRange ||
      this.weightUnitMissing ||
      this.customFieldFilterLimitExceeded
    )
      return
    this.clearCustomRangeTimer()
    this.filters = structuredClone(this.draft)
    this.appliedCustomFields = structuredClone(this.selectedCustomFields)
    this.filters.page = 1
    const wasOpen = this.filterPanelOpen
    this.filterPanelOpen = false
    if (wasOpen) {
      this.clearCustomFieldSearch()
      this.filterButton?.nativeElement.focus()
    }
    this.load()
  }

  resetFilters(): void {
    this.clearCustomRangeTimer()
    this.draft = defaultFilters()
    this.draft.search = this.filters.search
    this.draft.category = this.filters.category
    this.draft.subcategory = this.filters.subcategory
    this.draft.sort = this.filters.sort
    this.selectedCustomFields = []
    this.resetCustomRangeInputs()
    this.clearCustomFieldSearch()
    this.cancelCustomOptionRequests()
    this.brandSearch.setValue('')
    this.sellerSearch.setValue('')
    if (!this.filterPanelOpen) this.applyFilters()
  }

  get matchingBrands(): string[] {
    const query = this.brandSearch.value.trim().toLocaleLowerCase()
    return this.facets.brands
      .filter(
        (brand) =>
          !this.draft.brand.includes(brand) &&
          brand.toLocaleLowerCase().includes(query),
      )
      .slice(0, 8)
  }

  get matchingSellers(): MarketplaceFacets['suppliers'] {
    const query = this.sellerSearch.value.trim().toLocaleLowerCase()
    return this.facets.suppliers
      .filter(
        (seller) =>
          !this.draft.supplier.includes(seller.id) &&
          seller.name.toLocaleLowerCase().includes(query),
      )
      .slice(0, 8)
  }

  sellerName(id: string): string {
    return this.sellerNames.get(id) ?? id
  }

  selectBrand(brand: string): void {
    if (!this.draft.brand.includes(brand)) this.toggleChoice('brand', brand)
    this.brandSearch.setValue('')
  }

  selectSeller(id: string): void {
    if (!this.draft.supplier.includes(id)) this.toggleChoice('supplier', id)
    this.sellerSearch.setValue('')
  }

  get invalidRange(): boolean {
    return (
      this.invalidCustomRangeInputs.size > 0 ||
      this.draft.customFields.some(
        (f) =>
          (f.min != null && !Number.isFinite(f.min)) ||
          (f.max != null && !Number.isFinite(f.max)) ||
          (f.min != null && f.max != null && f.min > f.max),
      ) ||
      (this.draft.minPrice !== null &&
        this.draft.maxPrice !== null &&
        this.draft.minPrice > this.draft.maxPrice) ||
      (this.draft.minWeight !== null &&
        this.draft.maxWeight !== null &&
        this.draft.minWeight > this.draft.maxWeight)
    )
  }

  get weightUnitMissing(): boolean {
    return (
      (this.draft.minWeight !== null || this.draft.maxWeight !== null) &&
      !this.draft.weightUnit
    )
  }

  /**
   * Checks whether the selected values or encoded query exceed the API limits.
   * @returns {boolean} Whether the selected values exceed the API limits.
   */
  get customFieldFilterLimitExceeded(): boolean {
    if (
      this.draft.customFields.some(
        (field) => (field.values?.length ?? 0) > 1000,
      )
    )
      return true
    const customFields = serializeCustomFieldFilters(this.draft.customFields)
    return new TextEncoder().encode(customFields).length > 65535
  }

  filterChanged(): void {
    if (this.draft.minPrice !== null || this.draft.maxPrice !== null) {
      this.draft.currency ||= this.facets.currencies[0] ?? DEFAULT_CURRENCY
    }
    this.draft.page = 1
    if (this.invalidRange || this.weightUnitMissing) return
    if (!this.filterPanelOpen) this.applyFilters()
  }

  /**
   * Finds the draft condition for a selected attribute.
   * @param {CustomFieldFacet} facet Attribute whose draft condition is needed.
   * @returns {CustomFieldFilter | undefined} Matching draft condition, if one exists.
   */
  customFilter(facet: CustomFieldFacet): CustomFieldFilter | undefined {
    return this.draft.customFields.find((filter) =>
      this.sameCustomField(filter, facet),
    )
  }

  /**
   * Compares field keys, falling back to the normalized name, type and unit.
   * @param {CustomFieldFilter} a First field identity.
   * @param {CustomFieldFilter} b Field identity to compare.
   * @returns {boolean} Whether both fields refer to the same attribute.
   */
  private sameCustomField(a: CustomFieldFilter, b: CustomFieldFilter): boolean {
    if (a.key && b.key) return a.key === b.key
    return (
      normalizeCustomFieldIdentity(a.name) ===
        normalizeCustomFieldIdentity(b.name) &&
      a.type === b.type &&
      normalizeCustomFieldIdentity(a.unit ?? '') ===
        normalizeCustomFieldIdentity(b.unit ?? '')
    )
  }

  /**
   * Returns the draft condition, adding an empty one when the attribute is first selected.
   * @param {CustomFieldFacet} facet Attribute to find or add in the draft filters.
   * @returns {CustomFieldFilter} Existing or newly created draft condition.
   */
  private ensureCustomFilter(facet: CustomFieldFacet): CustomFieldFilter {
    let filter = this.customFilter(facet)
    if (!filter) {
      filter = {
        key: facet.key,
        name: facet.name,
        type: facet.type,
        unit: facet.unit,
        values: [],
      }
      this.draft.customFields.push(filter)
    }
    return filter
  }

  /**
   * Adds or removes a text or Yes/No choice from the draft filter.
   * @param {CustomFieldFacet} facet Attribute being filtered.
   * @param {string | boolean} value Text or Yes/No choice to toggle.
   * @returns {void} No return value.
   */
  toggleCustomChoice(facet: CustomFieldFacet, value: string | boolean): void {
    const filter = this.ensureCustomFilter(facet)
    const choices = filter.values ?? []
    filter.values = choices.includes(value)
      ? choices.filter((choice) => choice !== value)
      : [...choices, value]
    this.filterChanged()
  }

  /**
   * Stores a numeric bound and waits for typing to pause before applying it.
   * Incomplete input stays visible so the user can finish or correct it.
   * @param {CustomFieldFacet} facet Attribute being filtered.
   * @param {'min' | 'max'} bound Range endpoint being edited.
   * @param {HTMLInputElement} input Number input, including its browser validation state.
   * @returns {void} No return value.
   */
  customRangeChanged(
    facet: CustomFieldFacet,
    bound: 'min' | 'max',
    input: HTMLInputElement,
  ): void {
    this.clearCustomRangeTimer()
    if (input.validity.badInput) {
      this.invalidCustomRangeInputs.set(input, { facet, bound })
      return
    }
    this.invalidCustomRangeInputs.delete(input)
    this.ensureCustomFilter(facet)[bound] =
      input.value === '' ? null : input.valueAsNumber
    this.draft.page = 1
    if (this.filterPanelOpen || this.invalidRange || this.weightUnitMissing)
      return
    this.customRangeTimer = setTimeout(() => this.applyFilters(), 300)
  }

  /**
   * Cancels a pending numeric-range update.
   * @returns {void} No return value.
   */
  private clearCustomRangeTimer(): void {
    if (this.customRangeTimer) clearTimeout(this.customRangeTimer)
    this.customRangeTimer = undefined
  }

  /**
   * Restores invalid number inputs to their draft values and clears their validation state.
   * @returns {void} No return value.
   */
  private resetCustomRangeInputs(): void {
    for (const [input, { facet, bound }] of this.invalidCustomRangeInputs) {
      input.value = String(this.customFilter(facet)?.[bound] ?? '')
    }
    this.invalidCustomRangeInputs.clear()
  }

  /**
   * Shows the selected attribute name while leaving typed search text unchanged.
   * @param {string | CustomFieldSummary | null} value Typed search text or selected attribute.
   * @returns {string} Attribute name, search text or an empty string.
   */
  customFieldDisplay(value: string | CustomFieldSummary | null): string {
    return typeof value === 'string' ? value : (value?.name ?? '')
  }

  /**
   * Identifies a single-character query so the picker can show its search hint.
   * @returns {boolean} Whether the typed query contains a single character.
   */
  get customFieldSearchNeedsMore(): boolean {
    const query = this.customFieldSearch.value
    return typeof query === 'string' && query.trim().length === 1
  }

  /**
   * Checks whether the attribute picker has an empty text query.
   * @returns {boolean} Whether the attribute search text is blank.
   */
  get customFieldSearchEmpty(): boolean {
    const query = this.customFieldSearch.value
    return typeof query === 'string' && query.trim() === ''
  }

  /**
   * Checks whether all available attribute-filter slots are in use.
   * @returns {boolean} Whether 50 attribute filters are already selected.
   */
  get customFieldLimitReached(): boolean {
    return this.selectedCustomFields.length >= 50
  }

  /**
   * Debounces attribute lookup and excludes fields that are already selected.
   * @param {string | CustomFieldSummary} query Typed search text or the selected autocomplete option.
   * @param {boolean} [immediate] Starts lookup without the typing delay when true.
   * @returns {void} No return value.
   */
  searchCustomFields(
    query: string | CustomFieldSummary,
    immediate = false,
  ): void {
    if (typeof query !== 'string' || this.customFieldLimitReached) return
    if (this.customFieldSearchTimer) clearTimeout(this.customFieldSearchTimer)
    this.customFieldSearchRequest?.unsubscribe()
    this.customFieldSuggestions = []
    this.customFieldSearchHasMore = false
    this.customFieldSearchHasResults = false
    this.customFieldSearchFailed = false
    this.customFieldSearchLoading = true
    this.customFieldSearchTimer = setTimeout(
      () => {
        this.customFieldSearchRequest = this.marketplace
          .searchCustomFields(query)
          .subscribe({
            next: (response) => {
              this.customFieldSearchHasResults = response.results.length > 0
              this.customFieldSuggestions = response.results.filter(
                (field) =>
                  !this.selectedCustomFields.some(
                    (selected) => selected.key === field.key,
                  ),
              )
              this.customFieldSearchHasMore = response.hasMore
              this.customFieldSearchLoading = false
            },
            error: () => {
              this.customFieldSearchLoading = false
              this.customFieldSearchFailed = true
            },
          })
      },
      immediate ? 0 : 250,
    )
  }

  /**
   * Adds an attribute control and loads its options. An empty control does not narrow results.
   * @param {CustomFieldSummary} field Attribute selected from the suggestions.
   * @returns {void} No return value.
   */
  addCustomField(field: CustomFieldSummary): void {
    if (
      this.customFieldLimitReached ||
      this.selectedCustomFields.some((item) => item.key === field.key)
    )
      return
    const control: CustomFieldControl = {
      ...field,
      values: [],
      min: null,
      max: null,
      search: '',
      loading: false,
      failed: false,
      hasMore: false,
    }
    this.selectedCustomFields.push(control)
    const filter = this.ensureCustomFilter(control)
    this.clearCustomFieldSearch()
    this.loadCustomFieldOptions(control)
    // Empty controls stay selected without narrowing the product results.
    if (!this.filterPanelOpen) {
      this.filters.customFields.push(structuredClone(filter))
      this.appliedCustomFields = structuredClone(this.selectedCustomFields)
    }
  }

  /**
   * Removes an attribute control and its draft condition, then updates the results when the panel is closed.
   * @param {CustomFieldFacet} field Attribute control and condition to remove.
   * @returns {void} No return value.
   */
  removeCustomField(field: CustomFieldFacet): void {
    this.selectedCustomFields = this.selectedCustomFields.filter(
      (selected) => selected.key !== field.key,
    )
    this.draft.customFields = this.draft.customFields.filter(
      (filter) => !this.sameCustomField(filter, field),
    )
    this.cancelCustomOptionRequest(field.key)
    for (const [input, invalid] of this.invalidCustomRangeInputs) {
      if (invalid.facet.key === field.key)
        this.invalidCustomRangeInputs.delete(input)
    }
    this.filterChanged()
  }

  /**
   * Debounces text-value lookup, replacing any pending request for this attribute.
   * @param {CustomFieldControl} field Attribute control receiving the suggestions.
   * @param {string} query Text to search within the attribute values.
   * @returns {void} No return value.
   */
  searchCustomValues(field: CustomFieldControl, query: string): void {
    field.search = query
    this.cancelCustomOptionRequest(field.key)
    field.values = []
    field.hasMore = false
    field.loading = true
    field.failed = false
    this.customOptionTimers.set(
      field.key,
      setTimeout(() => this.loadCustomFieldOptions(field), 250),
    )
  }

  /**
   * Selects a text value and clears the search so other choices become available.
   * @param {CustomFieldControl} field Attribute control being edited.
   * @param {string} value Text value selected from the suggestions.
   * @returns {void} No return value.
   */
  selectCustomValue(field: CustomFieldControl, value: string): void {
    if (!this.customFilter(field)?.values?.includes(value))
      this.toggleCustomChoice(field, value)
    field.search = ''
    this.loadCustomFieldOptions(field)
  }

  /**
   * Returns suggested text values that have not already been selected.
   * @param {CustomFieldControl} field Attribute control containing the value suggestions.
   * @returns {string[]} Suggested text values that are not already selected.
   */
  matchingCustomValues(field: CustomFieldControl): string[] {
    return field.values.filter(
      (value): value is string =>
        typeof value === 'string' &&
        !this.customFilter(field)?.values?.includes(value),
    )
  }

  /**
   * Loads values or numeric bounds for an attribute and keeps its applied control up to date.
   * @param {CustomFieldControl} field Attribute control whose options should be refreshed.
   * @returns {void} No return value.
   */
  private loadCustomFieldOptions(field: CustomFieldControl): void {
    this.cancelCustomOptionRequest(field.key)
    field.loading = true
    field.failed = false
    this.customOptionRequests.set(
      field.key,
      this.marketplace.customFieldOptions(field, field.search).subscribe({
        next: (response) => {
          field.values = response.values
          field.min = response.min
          field.max = response.max
          field.hasMore = response.hasMore
          field.loading = false
          const applied = this.appliedCustomFields.find(
            (selected) => selected.key === field.key,
          )
          if (!this.filterPanelOpen && applied)
            Object.assign(applied, structuredClone(field))
        },
        error: () => {
          field.loading = false
          field.failed = true
        },
      }),
    )
  }

  /**
   * Cancels attribute lookup and clears the picker without starting another search.
   * @returns {void} No return value.
   */
  private clearCustomFieldSearch(): void {
    if (this.customFieldSearchTimer) clearTimeout(this.customFieldSearchTimer)
    this.customFieldSearchRequest?.unsubscribe()
    this.customFieldSearch.setValue('', { emitEvent: false })
    this.customFieldSuggestions = []
    this.customFieldSearchLoading = false
    this.customFieldSearchFailed = false
    this.customFieldSearchHasMore = false
    this.customFieldSearchHasResults = false
  }

  /**
   * Cancels the pending value lookup and typing delay for one attribute.
   * @param {string} key Attribute key identifying the pending lookup.
   * @returns {void} No return value.
   */
  private cancelCustomOptionRequest(key: string): void {
    const timer = this.customOptionTimers.get(key)
    if (timer) clearTimeout(timer)
    this.customOptionTimers.delete(key)
    this.customOptionRequests.get(key)?.unsubscribe()
    this.customOptionRequests.delete(key)
  }

  /**
   * Cancels all pending attribute-value lookups and typing delays.
   * @returns {void} No return value.
   */
  private cancelCustomOptionRequests(): void {
    for (const key of this.customOptionTimers.keys())
      this.cancelCustomOptionRequest(key)
    for (const key of this.customOptionRequests.keys())
      this.cancelCustomOptionRequest(key)
  }

  /**
   * Restores the applied controls when the filter panel opens or draft changes are discarded.
   * @returns {void} No return value.
   */
  private restoreCustomFieldControls(): void {
    this.cancelCustomOptionRequests()
    this.selectedCustomFields = structuredClone(this.appliedCustomFields)
    for (const field of this.selectedCustomFields) {
      if (field.loading || field.failed) this.loadCustomFieldOptions(field)
    }
  }

  /**
   * Returns applied attributes with a selected value or numeric bound.
   * @returns {CustomFieldFilter[]} Applied attribute conditions that narrow the product results.
   */
  get activeCustomFilters(): CustomFieldFilter[] {
    return this.filters.customFields.filter(
      (field) =>
        (field.values?.length ?? 0) > 0 ||
        field.min != null ||
        field.max != null,
    )
  }

  /**
   * Counts applied conditions for the filter badge, excluding empty attribute controls.
   * @returns {number} Number of applied filter choices and ranges.
   */
  get activeFilterCount(): number {
    const choices = [
      this.filters.brand,
      this.filters.supplier,
      this.filters.condition,
      this.filters.droneType,
      this.filters.videoResolution,
      this.filters.sensorSize,
      this.filters.flightRange,
      this.filters.fpv,
    ]
    let count = choices.reduce((total, values) => total + values.length, 0)
    count += this.activeCustomFilters.length
    if (
      this.filters.currency ||
      this.filters.minPrice != null ||
      this.filters.maxPrice != null
    )
      count++
    if (
      this.filters.weightUnit ||
      this.filters.minWeight != null ||
      this.filters.maxWeight != null
    )
      count++
    if (!this.filters.includeUnpriced) count++
    return count
  }

  /**
   * Formats an applied attribute condition for its removable filter chip.
   * @param {CustomFieldFilter} field Applied attribute condition to display.
   * @returns {string} Label containing the name, unit and selected values or bounds.
   */
  customConditionLabel(field: CustomFieldFilter): string {
    const heading = field.unit ? `${field.name} (${field.unit})` : field.name
    if (field.type !== 'number') {
      const values = (field.values ?? [])
        .map((value) => this.customValueLabel(value))
        .join(', ')
      return `${heading}: ${values}`
    }
    if (field.min != null && field.max != null)
      return `${heading}: ${field.min}–${field.max}`
    if (field.min != null)
      return `${heading}: ${this.t.getMarketplaceMinText()} ${field.min}`
    return `${heading}: ${this.t.getMarketplaceMaxText()} ${field.max}`
  }

  /**
   * Removes a condition from its applied chip, restores the controls and reloads the first page.
   * @param {CustomFieldFilter} field Applied attribute condition to remove.
   * @returns {void} No return value.
   */
  removeAppliedCustomFilter(field: CustomFieldFilter): void {
    this.filters.customFields = this.filters.customFields.filter(
      (filter) => !this.sameCustomField(filter, field),
    )
    this.appliedCustomFields = this.appliedCustomFields.filter(
      (selected) => !this.sameCustomField(field, selected),
    )
    this.draft = structuredClone(this.filters)
    this.restoreCustomFieldControls()
    this.resetCustomRangeInputs()
    this.filters.page = 1
    this.load()
  }

  /**
   * Includes the type and unit in an attribute label so similarly named fields can be distinguished.
   * @param {CustomFieldSummary} facet Attribute to describe.
   * @returns {string} Attribute name, translated type and optional unit.
   */
  customFacetLabel(facet: CustomFieldSummary): string {
    const label = `${facet.name}, ${this.customTypeLabel(facet)}`
    return facet.unit ? `${label}, ${facet.unit}` : label
  }

  /**
   * Returns the translated label for an attribute type.
   * @param {CustomFieldSummary} facet Attribute whose type should be displayed.
   * @returns {string} Translated label for the attribute type.
   */
  customTypeLabel(facet: CustomFieldSummary): string {
    if (facet.type === 'text') return this.t.getCustomFieldTextText()
    if (facet.type === 'number') return this.t.getCustomFieldNumberText()
    return this.t.getCustomFieldBooleanText()
  }

  /**
   * Keeps text choices as entered and translates boolean values to Yes or No.
   * @param {string | boolean} value Text or boolean choice to display.
   * @returns {string} Original text or a translated Yes/No label.
   */
  customValueLabel(value: string | boolean): string {
    if (typeof value === 'string') return value
    return value ? this.t.getYesText() : this.t.getNoText()
  }

  toggleChoice(
    key:
      | 'brand'
      | 'supplier'
      | 'condition'
      | 'droneType'
      | 'videoResolution'
      | 'sensorSize'
      | 'flightRange'
      | 'fpv',
    value: string,
  ): void {
    const choices = this.draft[key]
    this.draft[key] = choices.includes(value)
      ? choices.filter((item) => item !== value)
      : [...choices, value]
    this.filterChanged()
  }

  hasChoice(
    key:
      | 'brand'
      | 'supplier'
      | 'condition'
      | 'droneType'
      | 'videoResolution'
      | 'sensorSize'
      | 'flightRange'
      | 'fpv',
    value: string,
  ): boolean {
    return this.draft[key].includes(value)
  }

  pageChanged(event: PageEvent): void {
    this.filters.page = event.pageIndex + 1
    this.load()
  }

  select(product: MarketplaceProduct): void {
    if (this.marketplace.isSelected(product.id)) {
      this.marketplace.toggleSelection(product)
      return
    }
    const raw = this.quantityDrafts[product.id] ?? '1'
    const quantity = Number(raw)
    if (
      !raw.trim() ||
      !this.marketplace.validQuantity(quantity, product.requestUnit)
    ) {
      this.invalidQuantityId = product.id
      return
    }
    this.invalidQuantityId = null
    this.marketplace.toggleSelection(product, quantity)
  }

  quantityChanged(productId: string, event: Event): void {
    this.quantityDrafts[productId] = (event.target as HTMLInputElement).value
    if (this.invalidQuantityId === productId) this.invalidQuantityId = null
  }

  price(product: MarketplaceProduct): string {
    return product.priceAmount === null
      ? this.t.getMarketplacePriceNotProvidedText()
      : new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: product.priceCurrency ?? 'CAD',
          currencyDisplay: 'code',
        }).format(product.priceAmount)
  }

  categoryName(category: string): string {
    return this.t.getMarketplaceCategoryText(category)
  }

  subcategoryName(subcategory: string): string {
    return this.t.getMarketplaceSubcategoryText(subcategory)
  }

  flightLabel(range: string): string {
    if (range === 'under30') return this.t.getMarketplaceUnder30Text()
    if (range === '30to44') return this.t.getMarketplace30To44Text()
    return this.t.getMarketplace45PlusText()
  }
}
